import React, { useState, useEffect, useRef } from 'react';
import ModuleHeader from "../../moduleHeader";

const defaultPackageData = {
  user: {
    first_name: "Kullanıcı",
    last_name: ""
  },
  package: {
    name: "Breathing Technique",
    description: "4 saniye nefes al, 6 saniye ver. Bu teknik kalp ritmini yavaşlatır ve bilimsel olarak etkilidir. Stres ve anksiyeteyi azaltmak için kullanılır.",
    image_url: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80"
  }
};

export default function BreathingTechnique({ id, moduleId, isOwner }) {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('inhale'); // 'inhale', 'hold', 'exhale'
  const [timeLeft, setTimeLeft] = useState(4);
  const [cycleCount, setCycleCount] = useState(0);
  const [packageData] = useState(defaultPackageData);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const intervalRef = useRef(null);

  const INHALE_TIME = 4;
  const EXHALE_TIME = 6;

  useEffect(() => {
    if (isActive) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            if (phase === 'inhale') {
              setPhase('exhale');
              return EXHALE_TIME;
            } else {
              setPhase('inhale');
              setCycleCount(prev => prev + 1);
              return INHALE_TIME;
            }
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, phase]);

  const handleStart = () => {
    setIsActive(true);
    setPhase('inhale');
    setTimeLeft(INHALE_TIME);
    setCycleCount(0);
  };

  const handleStop = () => {
    setIsActive(false);
    setPhase('inhale');
    setTimeLeft(INHALE_TIME);
  };

  const handleReset = () => {
    setIsActive(false);
    setPhase('inhale');
    setTimeLeft(INHALE_TIME);
    setCycleCount(0);
  };

  const getCircleSize = () => {
    if (phase === 'inhale') {
      return 'scale-110';
    } else if (phase === 'exhale') {
      return 'scale-90';
    }
    return 'scale-100';
  };

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
            title={packageData.package?.name}
            description={packageData.package?.description}
            backgroundImage={packageData.package?.image_url}
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        <div className="w-full px-6 py-8 pb-24 flex-1 flex flex-col items-center justify-center">
          {/* Breathing Circle - Minimal Design */}
          <div className="mb-12">
            <div className={`relative w-72 h-72 mx-auto transition-all duration-${phase === 'inhale' ? INHALE_TIME : EXHALE_TIME}00 ease-in-out ${getCircleSize()}`}>
              {/* Outer ring */}
              <div className="absolute inset-0 rounded-full border-2 border-gray-200"></div>
              
              {/* Main circle */}
              <div className={`absolute inset-2 rounded-full bg-gradient-to-br ${
                phase === 'inhale' 
                  ? 'from-blue-100 to-blue-200' 
                  : 'from-green-100 to-green-200'
              } flex items-center justify-center transition-all duration-500`}>
                <div className="text-center">
                  <div className={`text-7xl font-light mb-3 transition-colors duration-300 ${
                    phase === 'inhale' ? 'text-blue-600' : 'text-green-600'
                  }`}>
                    {timeLeft}
                  </div>
                  <div className={`text-sm font-medium tracking-wide ${
                    phase === 'inhale' ? 'text-blue-500' : 'text-green-500'
                  }`}>
                    {phase === 'inhale' ? 'Nefes Al' : 'Nefes Ver'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cycle Counter - Minimal */}
          {isActive && (
            <div className="mb-8">
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1 tracking-wider">DÖNGÜ</p>
                <p className="text-2xl font-light text-gray-600">{cycleCount}</p>
              </div>
            </div>
          )}

          {/* Control Buttons - Minimal */}
          <div className="w-full space-y-2">
            {!isActive ? (
              <button
                onClick={handleStart}
                className="w-full bg-[#3DA7B5] hover:bg-[#2F8894] text-white rounded-full py-4 font-medium text-base transition-all duration-300 shadow-sm hover:shadow-md"
              >
                Başlat
              </button>
            ) : (
              <button
                onClick={handleStop}
                className="w-full bg-gray-400 hover:bg-gray-500 text-white rounded-full py-4 font-medium text-base transition-all duration-300 shadow-sm hover:shadow-md"
              >
                Durdur
              </button>
            )}
            {isActive && (
              <button
                onClick={handleReset}
                className="w-full bg-transparent border border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-700 rounded-full py-3 font-medium text-sm transition-all duration-300"
              >
                Sıfırla
              </button>
            )}
          </div>

          {/* Simple Instructions - Only when not active */}
          {!isActive && (
            <div className="mt-8 text-center">
              <p className="text-xs text-gray-400 leading-relaxed">
                4 saniye nefes al • 6 saniye nefes ver
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

